import { useState } from 'react';
import { Alert, Badge, Button, Divider, Group, Loader, Paper, Stack, Table, Text, Textarea, Title, Tooltip } from '@mantine/core';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { TransferResponse, TransferUpdate } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useCanOperate } from '../../../auth/permissions';
import { ConfirmButton } from '../../../components/ConfirmButton';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { AnyLocationSelect } from '../../../features/stores-transfers/components/AnyLocationSelect';
import { LocationName, ProductName } from '../../../features/stores-transfers/components/EntityNames';
import { LedgerPreview } from '../../../features/stores-transfers/components/LedgerPreview';
import { LineRowsEditor, type LineFormRow } from '../../../features/stores-transfers/components/LineRowsEditor';
import { formatQty } from '../../../features/stores-transfers/format';
import {
  useCreateTransfer,
  usePostTransfer,
  useReverseTransfer,
  useTransfer,
  useUpdateTransfer,
} from '../../../features/stores-transfers/api';

/** Owned by G24 (plan.md S0). Transfer detail: editable while DRAFT (edit route +
 * lines + Post, with the ledger-effect preview), read-only once POSTED with a
 * "Reverse & clone to draft" action once REVERSED per §4.1. */
export const Route = createFileRoute('/_authenticated/stores-transfers/transfers/$transferId')({
  component: TransferDetailPage,
});

function TransferDetailPage() {
  const { transferId } = Route.useParams();
  const { data: transfer, isLoading, isError } = useTransfer(transferId);

  if (isLoading) {
    return <Loader />;
  }
  if (isError || !transfer) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Transfer not found.
      </Alert>
    );
  }

  return transfer.status === 'DRAFT' ? <DraftTransferEditor key={transfer.id} transfer={transfer} /> : <PostedTransferView transfer={transfer} />;
}

function toLineFormRows(transfer: TransferResponse): LineFormRow[] {
  return transfer.lines.map((line) => ({
    key: line.id,
    productId: line.productId,
    productLabel: '',
    locationId: '',
    locationLabel: '',
    qty: line.qty,
  }));
}

function DraftTransferEditor({ transfer }: { transfer: TransferResponse }) {
  const canOperate = useCanOperate();
  const updateTransfer = useUpdateTransfer(transfer.id);
  const postTransfer = usePostTransfer(transfer.id);

  const [fromLocationId, setFromLocationId] = useState(transfer.fromLocationId);
  const [fromSiteId, setFromSiteId] = useState(transfer.fromSiteId);
  const [toLocationId, setToLocationId] = useState(transfer.toLocationId);
  const [toSiteId, setToSiteId] = useState(transfer.toSiteId);
  const [notes, setNotes] = useState(transfer.notes ?? '');
  const [lines, setLines] = useState<LineFormRow[]>(() => toLineFormRows(transfer));
  const [error, setError] = useState<string | null>(null);

  const linesValid = lines.length > 0 && lines.every((l) => l.productId && l.qty && Number(l.qty) > 0);
  const crossSite = Boolean(fromSiteId && toSiteId && fromSiteId !== toSiteId);

  function buildPayload(): TransferUpdate {
    return {
      fromLocationId,
      toLocationId,
      fromSiteId,
      toSiteId,
      notes: notes.trim() || null,
      lines: lines.map((l) => ({ productId: l.productId, qty: l.qty })),
    };
  }

  async function handleSave() {
    setError(null);
    try {
      await updateTransfer.mutateAsync(buildPayload());
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to save changes.');
    }
  }

  async function handlePost() {
    setError(null);
    try {
      await updateTransfer.mutateAsync(buildPayload());
      await postTransfer.mutateAsync(undefined);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to post the transfer.');
    }
  }

  return (
    <Stack gap="lg" maw={860}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{transfer.number}</Title>
          <DocStatusBadge status={transfer.status} />
        </Group>
        <Button component={Link} to="/stores-transfers" variant="subtle">
          Back to register
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="md">
          <AnyLocationSelect
            label="From location"
            value={fromLocationId}
            onChange={(id, _label, siteId) => {
              setFromLocationId(id);
              setFromSiteId(siteId);
            }}
            required
          />
          <AnyLocationSelect
            label="To location"
            value={toLocationId}
            excludeId={fromLocationId || undefined}
            onChange={(id, _label, siteId) => {
              setToLocationId(id);
              setToSiteId(siteId);
            }}
            required
          />
          {fromLocationId && toLocationId && (
            <Badge color={crossSite ? 'amber' : 'graphite'} variant="light" w="fit-content">
              {crossSite ? 'Cross-site transfer' : 'Same-site transfer'}
            </Badge>
          )}
          <Textarea label="Notes" value={notes} onChange={(event) => setNotes(event.currentTarget.value)} autosize minRows={2} />
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <LineRowsEditor rows={lines} onChange={setLines} siteId={fromSiteId || undefined} includeLocation={false} />
      </Paper>

      <Paper withBorder radius="md" p="md">
        <LedgerPreview
          siteId={fromSiteId || undefined}
          lines={lines}
          attribution={crossSite ? 'Cross-site — value moves between site pools' : 'Same-site — value stays within one site pool'}
        />
      </Paper>

      <Group justify="flex-end">
        <Tooltip label="Requires Operations role" disabled={canOperate}>
          <span>
            <Button
              variant="default"
              disabled={!canOperate}
              onClick={handleSave}
              loading={updateTransfer.isPending && !postTransfer.isPending}
            >
              Save changes
            </Button>
          </span>
        </Tooltip>
        <Tooltip label="Requires Operations role" disabled={canOperate}>
          <span>
            <Button onClick={handlePost} loading={postTransfer.isPending} disabled={!canOperate || !linesValid}>
              Post
            </Button>
          </span>
        </Tooltip>
      </Group>
    </Stack>
  );
}

function PostedTransferView({ transfer }: { transfer: TransferResponse }) {
  const canWrite = useCanOperate();
  const navigate = useNavigate();
  const reverseTransfer = useReverseTransfer(transfer.id);
  const createTransfer = useCreateTransfer();
  const [error, setError] = useState<string | null>(null);
  const [reversing, setReversing] = useState(false);
  const crossSite = transfer.fromSiteId !== transfer.toSiteId;

  async function handleReverseAndClone() {
    setError(null);
    setReversing(true);
    try {
      await reverseTransfer.mutateAsync(undefined);
      // "Clone" half of "reverse & clone to draft" (BUILD_SPEC.md §4.1) — see the
      // matching comment on the stores-issue detail screen for why the just-reversed
      // document's own header/lines are exactly what to prefill.
      const clone = await createTransfer.mutateAsync({
        fromLocationId: transfer.fromLocationId,
        toLocationId: transfer.toLocationId,
        fromSiteId: transfer.fromSiteId,
        toSiteId: transfer.toSiteId,
        notes: transfer.notes,
        lines: transfer.lines.map((line) => ({ productId: line.productId, qty: line.qty })),
      });
      void navigate({ to: '/stores-transfers/transfers/$transferId', params: { transferId: clone.id } });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to reverse this transfer.');
    } finally {
      setReversing(false);
    }
  }

  return (
    <Stack gap="lg" maw={860}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{transfer.number}</Title>
          <DocStatusBadge status={transfer.status} />
          <Badge color={crossSite ? 'amber' : 'graphite'} variant="light">
            {crossSite ? 'Cross-site' : 'Same-site'}
          </Badge>
        </Group>
        <Button component={Link} to="/stores-transfers" variant="subtle">
          Back to register
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Group gap={6}>
            <Text fw={600}>From:</Text>
            <LocationName id={transfer.fromLocationId} />
          </Group>
          <Group gap={6}>
            <Text fw={600}>To:</Text>
            <LocationName id={transfer.toLocationId} />
          </Group>
          {transfer.notes && (
            <Group gap={6} align="flex-start">
              <Text fw={600}>Notes:</Text>
              <Text>{transfer.notes}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>Qty</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {transfer.lines.map((line) => (
              <Table.Tr key={line.id}>
                <Table.Td>
                  <ProductName id={line.productId} />
                </Table.Td>
                <Table.Td>{formatQty(line.qty)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {transfer.status === 'POSTED' && (
        <>
          <Divider />
          <Group justify="flex-end">
            <Tooltip label="Requires Operations role" disabled={canWrite}>
              <span>
                <ConfirmButton
                  color="amber"
                  variant="light"
                  disabled={!canWrite}
                  loading={reversing}
                  label="Reverse & clone to draft"
                  message={`Reverse transfer ${transfer.number}? Its stock movement between locations${crossSite ? ' and sites' : ''} will be backed out by a reversing ledger entry, and a new DRAFT transfer will be created for you to redo.`}
                  onConfirm={handleReverseAndClone}
                />
              </span>
            </Tooltip>
          </Group>
        </>
      )}
      {transfer.status === 'REVERSED' && (
        <Text c="dimmed" size="sm">
          This transfer has been reversed. Its ledger effect has been undone; see the register for any draft that
          superseded it.
        </Text>
      )}
    </Stack>
  );
}
